import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore, useReadOnly, READ_ONLY_TITLE } from "@/state/store";
import { useWinningTopic } from "@/hooks/useWinningTopic";
import { EvalEventFeed } from "./EvalEventFeed";
import type {
  EvalEvent,
  Evaluation,
  RequirementFields,
  RevealEvalFlags,
  RevealState,
} from "@offsite/shared";

interface TeamMeta {
  teamNumber: number;
  teamName: string | null;
}

interface Props {
  /** "evaluating" still waiting for some teams to finish; "evaluated" all done. */
  phase: "evaluating" | "evaluated";
}

const EMPTY_FLAGS: RevealEvalFlags = {
  score: false,
  gut: false,
  ausbaufaehig: false,
};

/**
 * Unified carousel for the eval-results phases. While Haiku is still scoring
 * a team, that team's card shows a live EvalEventFeed. Once a team is done,
 * the card content blurs and a green check overlays — the actual reveal
 * (score / Stärken / Ausbaufähig) happens in the detail panel below via
 * presenter-controlled buttons. Reveal-flags persist in DDB so the
 * Reviewer-Magic-Link sees the same stage state.
 */
export function EvalCarousel({ phase }: Props) {
  const { state, dispatch } = useStore();
  const readOnly = useReadOnly();
  const winner = useWinningTopic();
  const token = state.auth.token;

  const [teams, setTeams] = useState<TeamMeta[]>([]);
  const [reqByTeam, setReqByTeam] = useState<Map<number, RequirementFields>>(
    new Map(),
  );
  const [evals, setEvals] = useState<Evaluation[]>([]);
  const [events, setEvents] = useState<EvalEvent[]>([]);
  const [reveal, setReveal] = useState<RevealState>({ eval: {}, output: {} });
  const [activeTeam, setActiveTeam] = useState<number | null>(null);
  const [advancing, setAdvancing] = useState(false);

  // Load + poll evaluations, events, reveal state.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [e, ev, rv] = await Promise.all([
          api.evaluations.list(token),
          api.eval.events(token),
          api.reveal.get(token),
        ]);
        if (cancelled) return;
        setEvals(e.evaluations);
        setEvents(ev.events);
        setReveal(rv.reveal);
      } catch {
        /* noop */
      }
    };
    load();
    const id = setInterval(load, 2000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  // Teams + their submitted requirements (frozen by this phase, load once).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.teams.list(token);
        if (cancelled) return;
        const list = r.teams.map((t) => ({
          teamNumber: t.number,
          teamName: t.name,
        }));
        setTeams(list);
        // Fetch requirements in parallel — small N.
        const reqs = await Promise.all(
          list.map(async (t) => {
            try {
              const res = await api.teams.getReq(token, t.teamNumber);
              return [t.teamNumber, res.requirement.fields] as const;
            } catch {
              return [t.teamNumber, null] as const;
            }
          }),
        );
        if (cancelled) return;
        const map = new Map<number, RequirementFields>();
        for (const [n, f] of reqs) if (f) map.set(n, f);
        setReqByTeam(map);
      } catch {
        /* noop */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (activeTeam !== null) return;
    if (teams.length > 0) setActiveTeam(teams[0].teamNumber);
  }, [teams, activeTeam]);

  const evalByTeam = new Map(evals.map((e) => [e.teamNumber, e]));
  const allDone =
    teams.length > 0 && teams.every((t) => evalByTeam.has(t.teamNumber));
  const doneCount = evals.length;

  const advanceToEvaluated = async () => {
    setAdvancing(true);
    try {
      const p = await api.phase.set(token, "evaluated");
      dispatch({ type: "phase/set", phase: p });
    } finally {
      setAdvancing(false);
    }
  };

  const startOvernight = async () => {
    setAdvancing(true);
    try {
      await api.overnight.trigger(token);
      const p = await api.phase.set(token, "overnight");
      dispatch({ type: "phase/set", phase: p });
    } finally {
      setAdvancing(false);
    }
  };

  const toggleReveal = async (
    teamNumber: number,
    section: keyof RevealEvalFlags,
  ) => {
    if (readOnly) return;
    const cur = reveal.eval[teamNumber] ?? EMPTY_FLAGS;
    const next = !cur[section];
    // Optimistic update.
    setReveal((r) => ({
      ...r,
      eval: { ...r.eval, [teamNumber]: { ...cur, [section]: next } },
    }));
    try {
      const res = await api.reveal.setEval(token, teamNumber, {
        [section]: next,
      });
      setReveal((r) => ({
        ...r,
        eval: { ...r.eval, [teamNumber]: res.eval },
      }));
    } catch {
      setReveal((r) => ({
        ...r,
        eval: { ...r.eval, [teamNumber]: cur },
      }));
    }
  };

  /** Click on a "done" card: select team + auto-reveal score (one-way). */
  const handleCardClick = (teamNumber: number, isDone: boolean) => {
    setActiveTeam(teamNumber);
    if (!isDone || readOnly) return;
    const cur = reveal.eval[teamNumber] ?? EMPTY_FLAGS;
    if (!cur.score) {
      // Reveal score on first click of a done card.
      void toggleReveal(teamNumber, "score");
    }
  };

  /** Toggle every flag for every team at once. Useful when the audience
   * is bored and the presenter wants to skip the slow reveal cadence. */
  const masterReveal = async (target: boolean) => {
    if (readOnly) return;
    const targets = teams.filter((t) => evalByTeam.has(t.teamNumber));
    // Optimistic.
    setReveal((r) => {
      const next = { ...r.eval };
      for (const t of targets) {
        next[t.teamNumber] = {
          score: target,
          gut: target,
          ausbaufaehig: target,
        };
      }
      return { ...r, eval: next };
    });
    await Promise.all(
      targets.map((t) =>
        api.reveal
          .setEval(token, t.teamNumber, {
            score: target,
            gut: target,
            ausbaufaehig: target,
          })
          .catch(() => undefined),
      ),
    );
  };

  // Are all flags for all done teams revealed?
  const doneTeams = teams.filter((t) => evalByTeam.has(t.teamNumber));
  const allRevealed =
    doneTeams.length > 0 &&
    doneTeams.every((t) => {
      const f = reveal.eval[t.teamNumber];
      return f && f.score && f.gut && f.ausbaufaehig;
    });

  const activeMeta = teams.find((t) => t.teamNumber === activeTeam) ?? null;
  const activeEval =
    activeTeam !== null ? evalByTeam.get(activeTeam) ?? null : null;
  const activeFlags =
    activeTeam !== null
      ? reveal.eval[activeTeam] ?? EMPTY_FLAGS
      : EMPTY_FLAGS;
  const activeReq =
    activeTeam !== null ? reqByTeam.get(activeTeam) ?? null : null;

  return (
    <div className="eval-board">
      <div className="deck-bg" aria-hidden="true" />

      <header className="eval-board-head">
        <div className="deck-eyebrow" style={{ color: "var(--svo-blau)" }}>
          {phase === "evaluating"
            ? "Workshop · Bewertung läuft"
            : "Workshop · Ergebnisse"}
        </div>
        {winner && (
          <div className="eval-board-topic">
            Thema: <strong>{winner.topic.title}</strong>
          </div>
        )}
        <h1 className="deck-title eval-board-title">
          {allDone
            ? phase === "evaluating"
              ? "Alle Teams bewertet"
              : "Wer bekommt den höchsten Score?"
            : `${doneCount} / ${teams.length || "…"} Teams bewertet`}
        </h1>
        {doneTeams.length > 0 && !readOnly && (
          <button
            type="button"
            className="reveal-master-btn"
            onClick={() => masterReveal(!allRevealed)}
            title={
              allRevealed
                ? "Alle Sektionen wieder verbergen"
                : "Alle Sektionen aller Teams enthüllen"
            }
          >
            {allRevealed ? "Alles verbergen" : "Alles enthüllen"}
          </button>
        )}
      </header>

      {/* Carousel — one card per team */}
      <div className="reveal-carousel">
        {teams.map((t) => {
          const ev = evalByTeam.get(t.teamNumber);
          const isActive = activeTeam === t.teamNumber;
          const status: "waiting" | "running" | "done" | "error" = ev
            ? "done"
            : (events
                  .filter((e) => e.teamNumber === t.teamNumber)
                  .some((e) => e.type === "error")
              ? "error"
              : events.some((e) => e.teamNumber === t.teamNumber)
                ? "running"
                : "waiting");
          const teamEvents = events.filter(
            (e) => e.teamNumber === t.teamNumber,
          );
          const fields = reqByTeam.get(t.teamNumber);
          const flags = reveal.eval[t.teamNumber] ?? EMPTY_FLAGS;
          const cardScoreRevealed = flags.score;
          return (
            <button
              key={t.teamNumber}
              type="button"
              className={`reveal-card is-${status}${isActive ? " is-active" : ""}${cardScoreRevealed ? " is-revealed" : ""}`}
              onClick={() => handleCardClick(t.teamNumber, status === "done")}
            >
              <div className="reveal-card-head">
                <div className="reveal-card-num">Team {t.teamNumber}</div>
                <div className="reveal-card-name">
                  {t.teamName ?? `Team ${t.teamNumber}`}
                </div>
              </div>

              <div className="reveal-card-body">
                {status === "done" && ev ? (
                  <>
                    <div
                      className={`reveal-card-content${cardScoreRevealed ? "" : " reveal-blur"}`}
                    >
                      <div className="reveal-card-score">
                        {ev.score.toFixed(1)}
                        <span className="max"> / 10</span>
                      </div>
                    </div>
                    {!cardScoreRevealed && (
                      <div className="reveal-card-check" aria-hidden="true">
                        ✓
                      </div>
                    )}
                  </>
                ) : status === "running" ? (
                  <>
                    <div className="reveal-card-content">
                      <RequirementSnippet fields={fields} dim />
                    </div>
                    <div className="reveal-card-feed">
                      <EvalEventFeed
                        events={teamEvents}
                        max={3}
                        variant="compact"
                      />
                    </div>
                  </>
                ) : status === "error" ? (
                  <div className="reveal-card-status">
                    <div className="reveal-card-content">
                      <RequirementSnippet fields={fields} dim />
                    </div>
                    <div className="reveal-card-error">⚠ Fehler</div>
                  </div>
                ) : (
                  <div className="reveal-card-status">
                    <div className="reveal-card-content">
                      <RequirementSnippet fields={fields} dim />
                    </div>
                    <div className="reveal-card-waiting">wartet…</div>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail panel for the selected team */}
      <div className="reveal-detail">
        {activeEval ? (
          <RevealDetail
            evaluation={activeEval}
            flags={activeFlags}
            requirement={activeReq}
            readOnly={readOnly}
            onToggle={(section) =>
              toggleReveal(activeEval.teamNumber, section)
            }
          />
        ) : activeMeta ? (
          <div className="reveal-detail-pending">
            <div className="eyebrow">
              {activeMeta.teamName ?? `Team ${activeMeta.teamNumber}`}
            </div>
            <h3 style={{ color: "var(--svo-weiss)", margin: "8px 0 0" }}>
              Bewertung läuft …
            </h3>
            <p
              className="form-hint"
              style={{
                color: "rgba(255,255,255,0.55)",
                marginTop: 12,
              }}
            >
              Sobald die KI fertig ist, wird die Bewertung hier verdeckt
              dargestellt — du enthüllst die Stücke einzeln.
            </p>
          </div>
        ) : null}
      </div>

      {/* Phase advance CTA */}
      {phase === "evaluating" ? (
        <div className="eval-board-cta">
          <button
            type="button"
            className={`btn ${allDone ? "btn-success" : "btn-disabled"} eval-board-cta-btn${readOnly ? " is-readonly" : ""}`}
            onClick={advanceToEvaluated}
            disabled={!allDone || advancing || readOnly}
            title={readOnly ? READ_ONLY_TITLE : undefined}
          >
            {advancing
              ? "…"
              : allDone
                ? "→ Zu den Ergebnissen"
                : `… ${doneCount} / ${teams.length || "?"} bewertet — bitte warten`}
          </button>
        </div>
      ) : (
        <div className="eval-board-cta">
          <button
            type="button"
            className={`btn btn-success eval-board-cta-btn${readOnly ? " is-readonly" : ""}`}
            onClick={startOvernight}
            disabled={readOnly || advancing}
            title={readOnly ? READ_ONLY_TITLE : undefined}
          >
            {advancing ? "…" : "→ Overnight-Job starten"}
          </button>
        </div>
      )}
    </div>
  );
}

function RequirementSnippet({
  fields,
  dim = false,
}: {
  fields: RequirementFields | null | undefined;
  dim?: boolean;
}) {
  if (!fields) {
    return (
      <div className="reveal-snippet" style={{ opacity: dim ? 0.5 : 1 }}>
        <em>(Kein Anforderungstext)</em>
      </div>
    );
  }
  return (
    <div className="reveal-snippet" style={{ opacity: dim ? 0.7 : 1 }}>
      <p>
        <strong>Was:</strong> {fields.was}
      </p>
      <p>
        <strong>Für wen:</strong> {fields.fuerWen}
      </p>
      <p>
        <strong>Problem:</strong> {fields.problem}
      </p>
    </div>
  );
}

function RevealDetail({
  evaluation,
  flags,
  requirement,
  readOnly,
  onToggle,
}: {
  evaluation: Evaluation;
  flags: RevealEvalFlags;
  requirement: RequirementFields | null;
  readOnly: boolean;
  onToggle: (section: keyof RevealEvalFlags) => void;
}) {
  const score = evaluation.score;
  const tone = score >= 7 ? "is-good" : score >= 4 ? "" : "is-warn";
  const pct = Math.max(0, Math.min(100, (score / 10) * 100));

  return (
    <div className="reveal-detail-inner">
      <div className="reveal-detail-head">
        <div className="eyebrow">
          Team {evaluation.teamNumber}
          {evaluation.teamName ? ` · ${evaluation.teamName}` : ""}
        </div>
      </div>

      {/* Always-visible: Zusammenfassung */}
      <blockquote className="eval-board-quote">
        „{evaluation.zusammenfassung}"
      </blockquote>

      {/* Score: blurred + button until revealed */}
      <RevealSection
        label="Score zeigen"
        revealed={flags.score}
        readOnly={readOnly}
        onToggle={() => onToggle("score")}
      >
        <div className="eval-board-score">
          <div className="num">
            {score.toFixed(1)}
            <span className="max"> / 10</span>
          </div>
          <div className="score-bar-track">
            <div
              className={`score-bar-fill ${tone}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </RevealSection>

      <div className="eval-board-cards">
        <RevealSection
          label="Was ist gut zeigen"
          revealed={flags.gut}
          readOnly={readOnly}
          onToggle={() => onToggle("gut")}
        >
          <div className="eval-board-card is-strengths">
            <h4>✓ Was ist gut</h4>
            <ul>
              {evaluation.gut.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
        </RevealSection>
        <RevealSection
          label="Was ist ausbaufähig zeigen"
          revealed={flags.ausbaufaehig}
          readOnly={readOnly}
          onToggle={() => onToggle("ausbaufaehig")}
        >
          <div className="eval-board-card is-improvements">
            <h4>△ Was ist ausbaufähig</h4>
            <ul>
              {evaluation.ausbaufaehig.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
        </RevealSection>
      </div>

      <footer className="eval-board-footer">
        Modell: <code>{evaluation.modelId}</code> ·{" "}
        {(evaluation.durationMs / 1000).toFixed(1)}s ·{" "}
        {evaluation.inputTokens}/{evaluation.outputTokens} Tokens
        {requirement && (
          <span style={{ marginLeft: 12 }}>
            · Anforderung: {requirement.was.slice(0, 60)}…
          </span>
        )}
      </footer>
    </div>
  );
}

function RevealSection({
  label,
  revealed,
  readOnly,
  onToggle,
  children,
}: {
  label: string;
  revealed: boolean;
  readOnly: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className={`reveal-section${revealed ? " is-revealed" : ""}`}>
      <div
        className={`reveal-section-content${revealed ? "" : " reveal-blur"}`}
        aria-hidden={!revealed}
      >
        {children}
      </div>
      {!revealed && (
        <button
          type="button"
          className="reveal-section-btn"
          onClick={onToggle}
          disabled={readOnly}
          title={readOnly ? READ_ONLY_TITLE : undefined}
        >
          {label}
        </button>
      )}
      {revealed && !readOnly && (
        <button
          type="button"
          className="reveal-section-hide"
          onClick={onToggle}
          title="wieder verbergen"
        >
          verbergen
        </button>
      )}
    </div>
  );
}
